import URI from 'urijs'
import get from 'lodash/get'
import noop from 'lodash/noop'
import endsWith from 'lodash/endsWith'
import gaEvents from './ga-events'
import leftArrowImage from '@/components/common/Krpano/img/markers/arrows/left-arrow.png'
import leftFrontArrowImage from '@/components/common/Krpano/img/markers/arrows/left-front-arrow.png'
import rightFrontArrowImage from '@/components/common/Krpano/img/markers/arrows/right-front-arrow.png'
import rightArrowImage from '@/components/common/Krpano/img/markers/arrows/right-arrow.png'
import frontArrowImage from '@/components/common/Krpano/img/markers/arrows/front-arrow.png'
import upStairsArrowImage from '@/components/common/Krpano/img/markers/arrows/upstairs-arrow.png'
import downStairsArrowImage from '@/components/common/Krpano/img/markers/arrows/downstairs-arrow.png'
import pointImage from '@/components/common/Krpano/img/markers/point.png'
import teleportImage from '@/components/common/Krpano/img/markers/teleport.png'
import memoImage from '@/components/common/Krpano/img/markers/memo.png'
import tagImage from '@/components/common/Krpano/img/markers/tag.png'
import galleryImage from '@/components/common/Krpano/img/markers/gallery.png'
import webImage from '@/components/common/Krpano/img/markers/web.png'
import linkImage from '@/components/common/Krpano/img/markers/link.png'
import videoImage from '@/components/common/Krpano/img/markers/video.png'
import voiceImage from '@/components/common/Krpano/img/markers/voice.png'
import multitagImage from '@/components/common/Krpano/img/markers/multitag.png'
import stickerImage from '@/components/common/Krpano/img/markers/sticker.png'
import searchImage from '@/components/common/Krpano/img/markers/search.png'
import pdfImage from '@/components/common/Krpano/img/markers/pdf.png'
import {
  getIEVersion,
  isEmpty,
  isIframe,
  xhr
} from './utils'
import { resizeImage } from '@/utils/resize'
import { HOTSPOT_BASE_SIZE } from '@/api/constants'

export const iStagingUrl = 'https://www.istaging.com'

export const handleUserComesFromWhereEvent = (to, callback = noop) => {
  const fullPath = to?.fullPath
  const buildingId = get(to, 'params.buildingId')
  const ref = get(to, 'query.ref')

  if (!buildingId) return false

  const gaActionMapping = {
    facebook: 'UrlClickedFromFacebook',
    twitter: 'UrlClickedFromTwitter',
    linkedin: 'UrlClickedFromLinkedin',
    qrcode: 'UrlClickedFromQRCode',
    yc: 'UrlClickedFromYungChingFullscreenButton',
    email: 'UrlClickedFromEmail',
    messenger: 'UrlClickedFromMessenger',
    line: 'UrlClickedFromLine',
    whatsapp: 'UrlClickedFromWhatsApp',
    wechat: 'UrlClickedFromWeChat',
    wecom: 'UrlClickedFromWeCom'
  }

  const matchedAction = gaActionMapping[ref]
  if (!ref) {
    gaEvents.sendEvent('Building', 'UrlClickedFromDirect', buildingId)
  } else if (matchedAction) {
    gaEvents.sendEvent('Building', matchedAction, buildingId)
  } else {
    gaEvents.sendEvent('Building', 'UrlClickedFromOther', buildingId)
  }

  const currentPathUri = new URI(fullPath)
  const nextRoutePath = currentPathUri.removeSearch('ref')
  callback(nextRoutePath.toString())
}

export const krpanoHelpers = {
  adjustKrpanoRendering (krpanoObj = null) {
    if (!krpanoObj) {
      return
    }
    krpanoObj.set('events[__threejs__].onviewchange', () => {
      // adjust krpano rendering
      if (krpanoObj.get('view.fisheye') !== 0.0) {
        // disable the fisheye distortion, ThreeJS objects can't be rendered with it
        krpanoObj.set('view.fisheye', 0.0)
      }
    }) // correct krpano view settings before the rendering
  }
}

export const genMarkerPosition = (deg) => {
  const position = {}
  const rad = deg * (Math.PI / 180)
  const x = -Math.sin(rad)
  const z = -Math.cos(rad)
  const y = Math.sin(rad)
  position.x = x * 200
  position.y = y * 200
  position.z = z * 200
  return position
}

export const coords3dTo2d = ({ x, y, z } = {}) => {
  const r = Math.sqrt(x * x + y * y + z * z)
  const ath = -Math.atan2(x, z) * 180 / Math.PI + 180
  const atv = -Math.asin(y / r) * 180 / Math.PI
  return { ath, atv }
}

export const coords2dTo3d = ({ ath, atv }) => {
  const x = -genMarkerPosition(ath).x
  const y = -genMarkerPosition(atv).y
  const z = genMarkerPosition(ath).z
  return { x, y, z }
}

export const isDevelopment = () => {
  return process.env.NODE_ENV === 'dev' ||
    process.env.NODE_ENV === 'test'
}

export const convertEscapeCharacters = (content = '') => {
  content = content.replace('%u003c', '%u0020&lt;%u0020') // <
  content = content.replace('%u003e', '%u0020&gt;%u0020') // >
  return content
}

// if s contains control character, it will let krpano broken
export const xmlUrlString = (s = '') => {
  s = s.replace(/&/g, '&amp;')
  s = s.replace(/</g, '&lt;')
  s = s.replace(/>/g, '&gt;')
  s = s.replace(/'/g, '&apos;')
  s = s.replace(/"/g, '&quot;')
  return s
}

export const xmlString = (s = '') => {
  if (!s) {
    return s
  }
  s = window.escape(s)
  // prevent escaped symbols like %26 (&) becomes to null (krpano treat %N as argument)
  s = s.replace(/%/g, '%%')
  return s
}

const ifCharIsUTF16 = (char = '') => {
  return char.codePointAt(0) >= 65536
}
const charToHexadecimal = (char) => {
  return '%u' + char.toString(16).padStart(4, '0')
}
const encodeStringToUTF16 = (str = '') => {
  return Array.from(str).map(char => {
    if (ifCharIsUTF16(char)) {
      return charToHexadecimal(char.charCodeAt(0)) + charToHexadecimal(char.charCodeAt(1))
    } else {
      return charToHexadecimal(char.charCodeAt(0))
    }
  }).join('')
}

export const returnProcessedContent = (str = '') => {
  if (str === '') return str
  str = krpanoEnterString(str)
  str = encodeStringToUTF16(str)
  str = convertEscapeCharacters(str)
  return str
}

export const krpanoEnterString = (string = '') => {
  return string.replace(/\n/g, '[br]')
}

export const krpanoAutoBlank = (string = '', n = 15, onlySingleLine = false) => { // n = max Latin-1 text in one line
  let stringAfterBlank
  stringAfterBlank = string.replace(/\[br\]/g, ' [br]')
  stringAfterBlank = stringAfterBlank.split(/[\n\t ]/g)
  stringAfterBlank = stringAfterBlank.map(word => {
    if (lengthInUtf8Bytes(word) >= (n * 2)) {
      for (let i = n; i <= word.length; i += (n + ' [br]'.length)) {
        if (onlySingleLine) {
          word = word.splice(i, 0, '... [br]')
        } else {
          word = word.splice(i, 0, ' [br]')
        }
      }
    }
    return word
  })
  stringAfterBlank = stringAfterBlank.join(' ')
  stringAfterBlank = stringAfterBlank.replace(/ \[br\]/g, '[br]')
  return stringAfterBlank
}

export const lengthInUtf8Bytes = (string = '') => {
  // Matches only the 10.. bytes that are non-initial characters in a multi-byte sequence.
  const m = encodeURIComponent(string).match(/%[89ABab]/g)
  return string.length + (m ? (m.length - 1) : 0)
}

export const setCorrectRotation = (value) => {
  // make rotation into -180 ~ 180
  while (value < -180 || value > 180) {
    if (value < -180) {
      value += 360
    }
    if (value > 180) {
      value -= 360
    }
  }

  return value
}

export const getParameterString = (routeQuery = {}) => {
  let parameter = ''
  const queries = Object.keys(routeQuery)
  if (queries && queries.length) {
    queries.forEach((query, index) => {
      index === 0
        ? parameter += '?'
        : parameter += '&'

      parameter += `${query}=${routeQuery[query]}`
    })
  }

  return parameter
}

export const imageIEHack = async (object = {}, keys = []) => {
  if (!isEmpty(object)) {
    await Promise.all(
      keys.map(async key => {
        if (object[key] && object[key].indexOf('getresizemapping') > -1) {
          const resp = await xhr(object[key], 'get')
          object[key] = JSON.parse(resp.target.response).url
          return resp
        }
      })
    )
  }
}

export const convertIndexFromUrlToArray = (index = 1, limit = Infinity) => {
  index = parseInt(index) // for user, panoramas is start from 1, not 0
  if (isNaN(index) || index <= 0) {
    index = 1
  }
  if (index > limit) {
    index = 0
  } else {
    index -= 1
  }
  return index
}

export const convertIndexFromArrayToUrl = (index = 0, limit = Infinity) => {
  index = parseInt(index) // for user, panoramas is start from 1, not 0
  if (isNaN(index) || index < 0) {
    index = 0
  }
  if (index > limit) {
    index = 1
  } else {
    index += 1
  }
  return index
}

export const getCorrectRotation = (value) => {
  // make rotation into -180 ~ 180
  while (value < -180 || value > 180) {
    if (value < -180) {
      value += 360
    }
    if (value > 180) {
      value -= 360
    }
  }
  return value
}

export const hotspotIcon = (marker = {}, useCustomIcon = false) => {
  if (useCustomIcon) {
    return `markerCustomIcon_${marker.objectId}`
  } else {
    switch (marker.type) {
      case 'point':
        switch (marker.iconType) {
          case 'leftArrow':
            return 'leftArrow'
          case 'leftFrontArrow':
            return 'leftFrontArrow'
          case 'rightFrontArrow':
            return 'rightFrontArrow'
          case 'rightArrow':
            return 'rightArrow'
          case 'frontArrow':
            return 'frontArrow'
          case 'upstairsArrow':
            return 'upstairsArrow'
          case 'downstairsArrow':
            return 'downstairsArrow'
          case 'point':
          default:
            return 'point'
        }
      case 'teleport':
        return 'teleport'
      case 'memo':
        return 'memo'
      case 'tag':
        return 'tag'
      case 'gallery':
        return 'gallery'
      case 'popup':
      case 'arMakerTag':
        switch (marker.iconType) {
          case 'link':
            return 'link'
          case 'video':
            return 'video'
          case 'web':
            return 'web'
          default:
            return 'web'
        }
      case 'pdf':
        return 'pdf'
      case 'narration':
        return 'narration'
      case 'multitag':
        return 'multitag'
      case 'sticker':
        return 'sticker'
      case 'search':
        return 'search'
      default:
    }
  }
}

export const getMarkerProfile = (marker) => {
  const iconTypeImage = {
    leftArrow: leftArrowImage,
    leftFrontArrow: leftFrontArrowImage,
    rightFrontArrow: rightFrontArrowImage,
    rightArrow: rightArrowImage,
    frontArrow: frontArrowImage,
    upstairsArrow: upStairsArrowImage,
    downstairsArrow: downStairsArrowImage,
    link: linkImage,
    video: videoImage,
    web: webImage
  }
  const markerImage = {
    point: pointImage,
    teleport: teleportImage,
    memo: memoImage,
    tag: tagImage,
    gallery: galleryImage,
    narration: voiceImage,
    popup: webImage,
    arMakerTag: webImage,
    multitag: multitagImage,
    sticker: stickerImage,
    search: searchImage,
    pdf: pdfImage
  }
  if (marker.photo) return marker.images ? marker.images[0] : marker.photo
  if (marker.useCustomIcon && marker.iconUrl) return marker.iconUrl
  if (marker.iconType && iconTypeImage[marker.iconType]) return iconTypeImage[marker.iconType]
  return markerImage[marker.type]
}

export const canLaunchPlanetView = () => !(getIEVersion() === 11 && isIframe())
export const getBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.readAsDataURL(file)
    reader.onload = () => resolve(reader.result)
    reader.onerror = error => reject(error)
  })
}

export const parseEmailToFirebaseKey = (email = '') => {
  if (!email) {
    return email
  }
  return email.replace(/[@.#!$%^&*()]/gm, '')
}

/**
 * 處理自定義 GIF icon 的設定
 * 由於renderer="css3d"，在載入或切換場景時，需要初始化alpha以及設定淡入效果
 * @param {*} marker
 * @returns
 */
export const generateCustomIconXml = (marker) => {
  if (marker.useCustomIcon && marker.iconUrl && /(\.gif)$/.test(marker.iconUrl)) {
    let opacity = isNaN(marker.opacity) ? 0.6 : (marker.opacity / 100)
    if (opacity * 100 <= 1) {
      opacity *= 100
    }

    const iconUrl = marker.disableResizing ? marker.iconUrl : resizeImage(marker.iconUrl)

    return `
      keep="true"
      renderer="css3d"
      onloaded="
      init_hotspot_alpha('marker_${marker.objectId}');
      add_custom_icon('${xmlUrlString(iconUrl)}', ${HOTSPOT_BASE_SIZE}, ${HOTSPOT_BASE_SIZE}, ${opacity});
      fade_in_hotspot_alpha('marker_${marker.objectId}', ${opacity});"
    `
  } else {
    return ''
  }
}

export const isUrlGif = (url) => {
  return endsWith(url, '.gif')
}
